// app/Application.js
'use strict'

import Project from './models/Project.js'
import CanvasState from './CanvasState.js'
import ToolManager from './ToolManager.js'
import SwatchSelectionState from './ui/SwatchSelectionState.js'
import Export from './Export.js'
import Utils from './Utils.js'
import PaintingListener from './listeners/PaintingListener.js'
import Color from './models/Color.js'
import Config from './Config.js'
import ControlsUI from './ui/ControlsUI.js'
import { DOM } from './ui/DOM.js'
import { Selector } from './ui/Selectors.js'
import Company from './models/Company.js'
import SwatchDataCache from './ui/SwatchDataCache.js'
import { renderCurrentSwatchSelector } from './ui/visualizer-swatch-panel.js'
import { renderTextureSwatchSelector } from './ui/visualizer-texture-swatch-panel.js'

import '../scss/index.scss'
import Dropcolor from './Dropcolor.js'

/**
 * Application = entry point and runtime owner.
 * - Owns UI, services, canvas, swatches, config, translations.
 * - Orchestrates workflows and calls into the Project model for saving.
 */
export default class Application {
    /**
     * -----------------------------------------------------------
     * Constructor (no JSON data here!)
     * -----------------------------------------------------------
     * Only receives fixed config like assetPath and documentRef.
     * Everything else is hydrated in initloader().
     */
    constructor({ documentRef = document, assetPath, uiAdapter }) {
        if (!assetPath) {
            throw new Error('Application: assetPath is required')
        }

        // Static configuration
        this.document = documentRef
        this.assetPath = assetPath // Will never change

        // Core models
        this.project = new Project()

        this.swatchSelection = new SwatchSelectionState()
        this.swatchUiAdapter = uiAdapter
        this.company = new Company()
        this.defaultTextureScale = Color.DEFAULT_TEXTURE_SCALE

        // Runtime objects created in resetVariables()
        this.canvasState = null
        this.toolManager = null
        this.exportObj = null
        this.paintingListener = null

        // Server-provided data (hydrated in initloader)
        this.translation = undefined
        this.config = undefined
        this.pdf_translation = undefined

        this.user_country_code = undefined
        this.user_logged_in = undefined
        this.user_verified = undefined
        this.application_verification_code = undefined

        this.dataProject = undefined

        this.sigAppName = undefined
        this.sigAppCode = undefined
        this.sigAppExtension = undefined
        this.currentLocale = undefined
        this.subdomain = undefined
        this.companyAssetFolder = undefined

        this.listOfColorChartsServerID = []
        this.listOfTextureSwatchServerID = []
        this.listOfColorCharts = []
        this.listOfTextureSwatches = []
        this.listOfInvisibleSwatchesServerID = []
        this.listOfInvisibleSwatches = []

        this.isProjectSaving = false
    }

    /**
     * Create a new project/photo on the backend when none exists yet.
     * Uses current source info (prepaint, uploaded image, or imported project)
     * and stores returned ids on project/dataProject.
     */
    async ensureProjectExists() {
        const photoObj = (this.dataProject?.photos || [])[0] || {}
        const projectIdValid = Number(this.project?.id) > 0
        let photoId = this.#extractPhotoId(photoObj)

        console.log('[Application] ensureProjectExists enter', {
            projectId: this.project?.id,
            dataProjectId: this.dataProject?.id,
            photoId,
        })

        // Already have a project id and photo → nothing to do
        if (projectIdValid && photoId) return

        // No usable photo id → create project/photo from current canvas
        const created = await this.#createProjectFromCanvas()
        photoId = created?.photo_id || photoId
        this.project.id = created?.project_id || created?.id || this.project.id

        if (!photoId) {
            console.error('[Application] ensureProjectExists failed', {
                projectId: this.project?.id,
                dataProjectId: this.dataProject?.id,
                hasPhotos: !!this.dataProject?.photos?.length,
                photoObj,
            })
            throw new Error('Cannot create project: missing photo id.')
        }

        // If project id was not returned, create it via model
        if (!this.project?.id) {
            const csrf = this.document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            const createdProject = await this.project.createProject({
                photoId,
                csrfToken: csrf,
            })
            this.project.id = createdProject?.id
        }

        // Persist ids locally for subsequent saves
        this.dataProject = {
            ...(this.dataProject || {}),
            id: this.project.id,
            photos: [{ id: photoId }],
        }
    }

    /**
     * Create project/photo by sending current canvas image to backend.
     * @private
     */
    async #createProjectFromCanvas() {
        const canvasEl = this.canvasState?.viewimage_original?.element
        if (!canvasEl) return null

        // Render canvas to JPEG blob (fallback to dataURL → blob)
        const blob = await new Promise((resolve, reject) => {
            if (canvasEl.toBlob) {
                canvasEl.toBlob(
                    (b) => {
                        if (b) resolve(b)
                        else reject(new Error('toBlob failed for canvas'))
                    },
                    'image/jpeg',
                    0.92,
                )
                return
            }
            try {
                const dataURL = canvasEl.toDataURL('image/jpeg', 0.92)
                fetch(dataURL)
                    .then((r) => r.blob())
                    .then(resolve, reject)
            } catch (e) {
                reject(e)
            }
        })

        const csrf = this.document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        const fd = new FormData()
        fd.append('original', blob, 'canvas.jpg')

        const res = await fetch('/projects/store-from-image', {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': csrf || '',
                'X-Requested-With': 'XMLHttpRequest',
                Accept: 'application/json',
            },
            body: fd,
        })

        if (!res.ok) {
            const text = await res.text()
            throw new Error(text || 'Image upload failed (canvas)')
        }

        const data = await res.json()
        if (data?.photo_id) {
            this.dataProject = {
                ...(this.dataProject || {}),
                photos: [{ id: data.photo_id }],
            }
        }
        if (data?.project_id || data?.id) {
            this.project.id = data.project_id || data.id
            this.dataProject = {
                ...(this.dataProject || {}),
                id: this.project.id,
                photos: this.dataProject?.photos || [{ id: data.photo_id }],
            }
        }
        return data
    }

    /**
     * Extract a photo id from the current API photo payload shape.
     * @param {{id?:number, photo_id?:number}} obj
     * @returns {number|null}
     */
    #extractPhotoId(obj = {}) {
        const n = Number(obj.photo_id ?? obj.id)
        return Number.isFinite(n) && n > 0 ? n : null
    }

    /**
     * Populate a select element with [label, id] entries and mark the selected id.
     * @param {string} selector
     * @param {Array<[string, number|string]>} items
     * @param {number|string} selectedId
     */
    #populateSelectOptions(selector, items, selectedId) {
        const $el = $(selector)
        $el.empty()
        items.forEach(([label, id]) => {
            const selected = Number(selectedId) === Number(id) ? 'selected' : ''
            $el.append(`<option ${selected} value="${id}">${label}</option>`)
        })
    }

    /**
     * -----------------------------------------------------------
     * initloader(data)
     * -----------------------------------------------------------
     * Bootstraps the app with server JSON.
     *
     * Steps:
     * 1. Load ALL JSON data (translations, config, user, brand)
     * 2. Hydrate project info
     * 3. Call resetVariables() to create canvas + tool manager
     * 4. Load swatches (async)
     * 5. Populate selectors & swatch UI
     * 6. Setup listeners, painting tools, responsive canvas
     * 7. If project from DB → hydrate canvas from DB + preview
     */
    async initloader(data) {
        console.log('initloader')

        /*----------------------------------------------------------
         1. HYDRATE ALL JSON DATA
        ----------------------------------------------------------*/
        this.translation = data.trans
        this.config = data.config
        const textureScale = Number(this.config?.visualizer?.defaults?.textureScale)
        this.defaultTextureScale = textureScale > 0 ? textureScale : Color.DEFAULT_TEXTURE_SCALE
        this.pdf_translation = data.pdf_translation
        this.user_country_code = data.user_country_code
        this.user_logged_in = data.user_logged_in
        this.user_verified = data.user_verified
        this.application_verification_code = data.application_verification_code

        // Branding / signature info
        if (data.assetPath && data.assetPath !== this.assetPath) {
            console.warn('Application: assetPath mismatch between constructor and JSON')
        }

        this.companyAssetFolder = data.webSimulator.companyAssetFolder
        this.sigAppName = data.webSimulator.exportCompanyName
        this.sigAppExtension = data.webSimulator.exportExtension
        this.sigAppCode = data.webSimulator.exportSignature
        this.subdomain = data.webSimulator.subdomain
        this.currentLocale = data.currentLocale

        this.company = Company.fromSources({
            webSimulator: data.webSimulator,
            company_address: data.company_address,
        })

        // Swatch lists
        this.listOfColorChartsServerID = Object.values(data.swatches || {})
        this.listOfTextureSwatchServerID = Object.values(data.textureSwatches || {})
        this.listOfColorCharts = Object.entries(data.swatches || {})
        this.listOfTextureSwatches = Object.entries(data.textureSwatches || {})
        this.listOfInvisibleSwatchesServerID = Object.values(data.swatches_invisible || {})
        this.listOfInvisibleSwatches = Object.entries(data.swatches_invisible || {})

        /*----------------------------------------------------------
         2. HYDRATE PROJECT MODEL
        ----------------------------------------------------------*/
        if (data.project) {
            const p = data.project
            this.dataProject = p

            const hydrated = Project.fromAPI(p)
            this.project.id = hydrated.id
            this.project.name = hydrated.name
            this.project.firstname = hydrated.firstname
            this.project.lastname = hydrated.lastname
            this.project.notes = hydrated.notes
            this.project.address_id = hydrated.address_id
            this.project.user_id = hydrated.user_id
            this.project.address = hydrated.address
        } else {
            this.dataProject = null
        }

        this.project.name = this.project.name || data.new_project_name

        /*----------------------------------------------------------
         3. INITIALIZE RUNTIME (canvas + tools)
        ----------------------------------------------------------*/
        this.resetVariables()

        /*----------------------------------------------------------
         4. LOAD SWATCHES (async)
        ----------------------------------------------------------*/
        const dataCache = SwatchDataCache.getInstance()
        // Warm the swatch cache in the background to avoid repeat fetches.
        dataCache.loadAll({
            swatches: data.swatches || {},
            swatchesInvisible: data.swatches_invisible || {},
            textureSwatches: data.textureSwatches || {},
        })

        this.swatchSelection.currentSwatch = await dataCache.getSwatchOrFetch(data.selectedSwatchID)
        this.swatchSelection.currentSwatchId = data.selectedSwatchID

        if (data.selectedTextureSwatchID) {
            this.swatchSelection.currentTextureSwatchId = data.selectedTextureSwatchID
            this.swatchSelection.currentTextureSwatch = await dataCache.getTextureSwatchOrFetch(data.selectedTextureSwatchID)
        }

        /*----------------------------------------------------------
         5. POPULATE SELECTORS
        ----------------------------------------------------------*/
        this.#populateSelectOptions('#selectcc', this.listOfColorCharts, this.swatchSelection.currentSwatchId)
        this.#populateSelectOptions('#selectte', this.listOfTextureSwatches, this.swatchSelection.currentTextureSwatchId)

        if (this.listOfTextureSwatches.length === 0) {
            DOM.hide(Selector.scrollerEffects)
        }

        // Load swatches into right UI area
        renderCurrentSwatchSelector(this)

        if (this.listOfTextureSwatches.length > 0) {
            renderTextureSwatchSelector(this)
        }

        /*----------------------------------------------------------
         6. DEVICE TWEAKS
        ----------------------------------------------------------*/
        if (Utils.isiPhoneAndiPad(navigator)) DOM.hide(Selector.btnExport)
        if (Utils.isTouchScreenDevice(navigator)) {
            DOM.get(Selector.colorchartselectors).classList.add('colorchart-small-device')
            DOM.get(Selector.textureswatchselectors).classList.add('colorchart-small-device')
        }

        /*----------------------------------------------------------
         7. PAINTING LISTENER
        ----------------------------------------------------------*/
        this.paintingListener = new PaintingListener(this, this.document)

        /*----------------------------------------------------------
         8. HYDRATE FROM DATABASE IF PROJECT EXISTS
        ----------------------------------------------------------*/
        if (typeof this.dataProject?.id !== 'undefined') {
            this.exportObj.initExportFromDatabase(this.dataProject, this)
            this.projectID = this.dataProject.id

            await this.canvasState.loadProjectFromDatabase(
                this.dataProject,
                this.toolManager,
                this.swatchSelection.currentSwatchId,
                this.listOfColorChartsServerID,
            )

            this.#showPreviewSection()
        }

        /*----------------------------------------------------------
         9. CONTROLS UI (sliders / tools)
        ----------------------------------------------------------*/
        const ui = new ControlsUI({
            toolManager: this.toolManager,
            config: {
                opacity: { max: 50, min: 1, step: 1, value: Color.DEFAULT_BLEACH_VALUE },
                wheel: { max: Config.MAGIC_WAND_MAX_LEVEL, min: 1, step: 1, value: Config.MAGIC_WAND_DEFAULT_LEVEL },
                rotate: { max: 90, min: -90, step: 1, value: Color.DEFAULT_TEXTURE_ROTATION },
                scale: { max: 1.5, min: 0.15, step: 0.02, value: this.defaultTextureScale },
                brush: { max: 40, min: 10, step: 10, value: 30 },
            },
        })

        if (this.document.readyState === 'loading') {
            this.document.addEventListener('DOMContentLoaded', () => ui.init(), { once: true })
        } else {
            ui.init()
        }

        /*----------------------------------------------------------
         10. RESPONSIVE CANVAS LISTENERS (set once)
        ----------------------------------------------------------*/
        window.addEventListener('resize', () => this.canvasState.reportWindowSize())
        window.addEventListener('orientationchange', () => this.canvasState.reportWindowSize())
    }

    /**
     * Creates a FULLY FRESH runtime state:
     *  - new CanvasState
     *  - new ToolManager (always valid because assetPath comes from constructor)
     *  - new Export using signature info set in initloader
     *
     * Safe to call before UI is loaded and repeatedly during runtime
     * when a new image is uploaded or prepaint is selected.
     */
    resetVariables() {
        console.log('----- resetVariables -----')

        // 1. CanvasState: create once, then reset
        if (!this.canvasState) {
            this.canvasState = new CanvasState('main', {
                defaultTextureScale: this.defaultTextureScale,
                onDropcolorCreated: (color) => {
                    console.log('Dropcolor created from DB/prepaint:', color)
                },
            })
        } else {
            this.canvasState.resetForNewProject()
        }

        // 2. ToolManager: create once, then reset (and re-bind canvasState)
        if (!this.toolManager) {
            this.toolManager = new ToolManager({
                canvasState: this.canvasState,
                project: this,
                document: this.document,
                assetPath: this.assetPath,
            })
        } else {
            this.toolManager.resetForNewProject({ canvasState: this.canvasState })
        }

        // 3. Export object recreated for each project/session
        this.exportObj = new Export(this.sigAppName, this.sigAppCode, this.sigAppExtension)

        // 4. UI resets
        DOM.hide(Selector.btnRotate)
        Dropcolor.resetSlots()
    }

    /**
     * Orchestrates post-render behavior (masks + preview).
     * This used to live in Project; by responsibility, it belongs here.
     */
    async finalizeAfterPhotoRender(photoDTO, canvasResult) {
        const { masks, maskTextureImageData } = canvasResult

        this.toolManager.initImageSegmentationProcessor()

        // No mask texture → just recompute and show preview
        if (!photoDTO.maskB64) {
            await this.canvasState.rebuildPaintedImage()
            this.#showPreviewSection()
            return
        }

        // Mask texture present
        const hasMasks = Array.isArray(masks) && masks.length > 0
        if (hasMasks) {
            await this.canvasState.applyMaskColorSampleAndTexture({
                colorArray: masks,
                maskTextureImageData: maskTextureImageData,
                current_swatch_id: this.swatchSelection.currentSwatchId,
                listOfColorChartsServerID: this.listOfColorChartsServerID,
            })
        } else {
            await this.canvasState.rebuildPaintedImage()
        }

        this.#showPreviewSection()
    }

    /**
     * Orchestrates saving the rendered image + mask via Project.saveProject.
     * UI and canvas work stay here; networking is delegated to the model.
     */
    async saveProject() {
        console.log('saveProject')

        // Require an existing project + photo before saving updates
        if (!this.project?.id || !this.dataProject?.photos?.[0]?.id) {
            return
        }
        DOM.loading()
        if (this.isProjectSaving) return
        this.isProjectSaving = true

        // Local helpers kept inline to avoid extra class methods
        const canvasToBlob = (canvas, type = 'image/jpeg', quality = 0.92) =>
            new Promise((resolve, reject) => {
                if (!canvas) return resolve(null)
                if (canvas.toBlob) {
                    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))), type, quality)
                } else {
                    try {
                        const dataURL = canvas.toDataURL(type, quality)
                        fetch(dataURL)
                            .then((r) => r.blob())
                            .then(resolve, reject)
                    } catch (e) {
                        reject(e)
                    }
                }
            })

        const renderMaskToCanvas = () => {
            const base = this.canvasState?.viewimage_original
            const mask = this.canvasState?._maskImage
            if (!base || !mask?.data) return null
            const w = base.width,
                h = base.height
            const c = document.createElement('canvas')
            c.width = w
            c.height = h
            const ctx = c.getContext('2d', { alpha: false })
            const imgData = ctx.createImageData(w, h)
            const pixels = mask.length ?? mask.data.length
            for (let i = 0; i < pixels; i++) {
                const pos = i * 4,
                    v = mask.data[i] | 0
                imgData.data[pos] = v
                imgData.data[pos + 1] = v
                imgData.data[pos + 2] = v
                imgData.data[pos + 3] = 255
            }
            ctx.putImageData(imgData, 0, 0)
            return c
        }

        try {
            const photoId = this.dataProject?.photos?.[0]?.id
            if (!photoId) throw new Error('Missing photoId.')

            const csrf = this.document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            const colorSampleJSON = JSON.stringify(this.canvasState.colorSample, null, 2)
            const previewCanvas = DOM.get(Selector.canvasMain)
            const maskCanvas = renderMaskToCanvas()

            const [previewBlob, maskBlob] = await Promise.all([
                canvasToBlob(previewCanvas, 'image/jpeg', 0.92),
                maskCanvas ? canvasToBlob(maskCanvas, 'image/png') : Promise.resolve(null),
            ])

            if (!previewBlob) throw new Error('Preview canvas is missing or empty.')
            if (!maskBlob) throw new Error('Mask data is missing or empty.')

            await this.project.saveProject({
                photoId,
                colorSampleJSON,
                previewBlob,
                maskBlob,
                csrfToken: csrf,
            })
        } finally {
            this.isProjectSaving = false
            DOM.loading(false)
        }
    }

    /**
     * Orchestrates saving form-only project info via Project.saveProjectInfos.
     * - Reads current DOM values by ID from the project info form.
     * - Lets Project handle normalization and the AJAX request.
     */
    async saveProjectInfos({ formEl } = {}) {
        console.log('saveProjectInfos')

        if (!this.project.id) {
            return
        }

        DOM.loading()
        this.isProjectSaving = true

        try {
            const csrf = this.document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

            await this.project.saveProjectInfos({ projectId: this.project.id, csrfToken: csrf, formEl })

            return true
        } finally {
            this.isProjectSaving = false
            DOM.loading(false)
        }
    }

    /**
     * Minimal private UI method to keep parity with current UX.
     * Shows preview area, updates subtitles, resizes canvas, etc.
     */
    #showPreviewSection() {
        console.log('showPreviewSection')

        DOM.showIfExists(Selector.preview, this.document) || DOM.showIfExists('#viz-canvas', this.document)
        DOM.show(Selector.colorchartrow)
        DOM.scrollToId('viz-canvas', { offset: 0, behavior: 'auto' })

        // Rebuild dropcolors + layout safely even if some optional nodes are missing.
        Dropcolor.rebuildSlots()
        this.canvasState.setNewCanvasDimension()
        this.canvasState.colorSample.updateColorInfos()

        PaintingListener.addTouchEventToDropColor()
        DOM.loading(false)
    }

    alertColorNotSelected() {
        alert($('<div />').html(this.translation.error_click_color_not_selected).text())
    }
}

window.Application = Application
